import { useCallback, useMemo, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Grid, styled, useTheme } from '@mui/material';
import { BasicButton, StatusChip, Typography } from '../../components/atoms';
import { Banner } from '../../components/molecules/Banner/Banner';
import { InfoTooltip } from '../../components/molecules/InfoTooltip';
import { StickyRegion } from '../../containers/sticky-region';
import { useAppDispatch, useAppSelector } from '../../common/hooks';
import { updateFactsheetModalVisibility, updateGlobalLoaderState } from '../../features/global/globalSlice';
import { STATUSVARIANTS } from '../../common/constants';
import { ReactComponent as ArrowDownTrayIcon } from '../../assets/icons/arrow_down_tray.svg';
import { MarketPortfolios, Portfolio, TenantSupportedWeightType } from '../../common/types';
import { PdfDownloadToastState } from '../../utils';
import PlanExecutionStep from './PlanExecutionStep';
import { StalePlanBanner } from '../../components/molecules/StalePlanBanner/StalePlanBanner';

const StyledBanner = styled('div')(({ theme }) => ({
    paddingBlock: theme.spacing(1)
}));

const SavePlanButton = styled(BasicButton)({
    height: '38px',
    padding: '8px 16px',
    borderRadius: '999px',
    fontWeight: 400,
    lineHeight: '22px'
});

const StyledBasicButton = styled(BasicButton)<{ $isEnabled?: boolean }>(({ theme, $isEnabled }) => ({
    height: '38px',
    padding: '8px 16px',
    gap: '8px',
    borderRadius: '999px',
    fontWeight: 400,
    lineHeight: '22px',
    backgroundColor: theme.palette.semantic.bg.default,
    color: theme.palette.semantic.int.selected,
    border: `2px solid ${theme.palette.semantic.int.selected}`,
    '&:hover': {
        backgroundColor: theme.palette.semantic.bg.default,
        color: theme.palette.semantic.int.selected,
        border: `2px solid ${theme.palette.semantic.int.selected}`
    },
    '&.Mui-disabled': {
        border: 'none'
    },
    '& path': {
        fill: $isEnabled ? theme.palette.semantic.int.selected : theme.palette.neutral[300]
    }
}));

const StyleDivWithStatus = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2)
}));

const checkUrlsInSource = (source: any) => {
    if (!Array.isArray(source) || source.length === 0) {
        return { allValid: false, fewValid: false };
    }

    let allValid = true;
    let fewValid = false;

    for (const portfolio of source) {
        const url = portfolio.portfolioUrl;
        const isValidUrl = !!url && url !== 'N/A' && url.startsWith('http');
        if (isValidUrl) {
            fewValid = true;
        } else {
            allValid = false;
        }
    }
    return { allValid: allValid, fewValid: fewValid };
};

interface PlanExecutionSectionProps {
    // eslint-disable-next-line no-unused-vars
    updateStep?: (step: number | ((prev: number) => number)) => void;
    tactical: Portfolio[];
    strategic: Portfolio[];
    weightTypes: TenantSupportedWeightType;
    isPlanExecutionReady: boolean;
    goalsWithMissingDates?: any[];
    goalDataRecomendation?: any;
    savePlanError: boolean;
    // AEX-3363: when the Save Plan was rejected because the plan changed in another tab, show the
    // Refresh Plan banner in place of the generic save-failed error.
    isStalePlan?: boolean;
    onRefreshPlan?: () => void;
    supportEmail: string;
    openPDFModal: boolean;
    // eslint-disable-next-line no-unused-vars
    setOpenPDFModal: (value: boolean) => void;
    // eslint-disable-next-line no-unused-vars
    setFactSheetsMissingToast: (value: { open: boolean; generating: boolean }) => void;
    setPdfGenerateToast: React.Dispatch<React.SetStateAction<PdfDownloadToastState>>;
    triggerBackendDownload: () => Promise<void>;
    handleSavePlan: () => Promise<void>;
    // eslint-disable-next-line no-unused-vars
    triggerPlanExecution: (forceRefresh?: boolean) => Promise<void>;
}

// Module-scope component so its identity is stable across ClientJourneyFlow2 re-renders. When it
// was defined inline inside ClientJourneyFlow2, every parent render produced a new function type,
// which made StepperPills unmount/remount PlanExecutionStep and reset its local state.
const PlanExecutionSection = ({
    tactical,
    strategic,
    weightTypes,
    isPlanExecutionReady,
    goalsWithMissingDates,
    goalDataRecomendation,
    savePlanError,
    isStalePlan,
    onRefreshPlan,
    supportEmail,
    openPDFModal,
    setOpenPDFModal,
    setFactSheetsMissingToast,
    setPdfGenerateToast,
    triggerBackendDownload,
    handleSavePlan,
    triggerPlanExecution
}: PlanExecutionSectionProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const adviceGoals = useAppSelector((state) => state.adviceGoals);
    const adviceClient = useAppSelector((state) => state.adviceClient);
    const marketPortfolios: MarketPortfolios = useAppSelector((state) => state.global.marketPortfolios) as any;

    const modalVisibilityFlags = useMemo(() => {
        if (marketPortfolios?.showModal || marketPortfolios?.showBanner || marketPortfolios?.showPdfViewer) {
            return {
                showModal: marketPortfolios.showModal,
                showBanner: marketPortfolios.showBanner,
                showPdfViewer: marketPortfolios.showPdfViewer
            };
        }
        const tacticalUrlsStatus = checkUrlsInSource(tactical);
        const strategicUrlsStatus = checkUrlsInSource(strategic);

        let modal = false;
        let banner = false;
        let pdfViewer = false;

        if (weightTypes === 'both') {
            const allUrlsPresent = tacticalUrlsStatus.allValid && strategicUrlsStatus.allValid;
            const fewUrlsPresent = tacticalUrlsStatus.fewValid || strategicUrlsStatus.fewValid;

            if (allUrlsPresent) {
                modal = true;
            } else if (fewUrlsPresent) {
                banner = true;
            } else {
                pdfViewer = true;
            }
        } else if (weightTypes === 'strategic_only') {
            if (strategicUrlsStatus?.allValid) {
                modal = true;
            } else if (strategicUrlsStatus?.fewValid) {
                banner = true;
            } else {
                pdfViewer = true;
            }
        }
        dispatch(updateFactsheetModalVisibility({ showModal: modal, showBanner: banner, showPdfViewer: pdfViewer }));
        return { showModal: modal, showBanner: banner, showPdfViewer: pdfViewer };
    }, [tactical, strategic, weightTypes]);

    const handlePdfGenerationClick = async () => {
        if (modalVisibilityFlags?.showModal) {
            setOpenPDFModal(true);
            setFactSheetsMissingToast({ open: false, generating: false });
        } else if (modalVisibilityFlags?.showBanner) {
            setOpenPDFModal(false);
            setPdfGenerateToast({ open: true, generating: true });
            dispatch(updateGlobalLoaderState(true));
            try {
                await triggerBackendDownload();
            } finally {
                dispatch(updateGlobalLoaderState(false));
                // Safety net: if the download path never resolved the toast,
                // dismiss the in-progress one so it doesn't stay stuck.
                setPdfGenerateToast((prev) => (prev.generating ? { open: false, generating: false } : prev));
                // Surface the missing toast even if the download rejects.
                setFactSheetsMissingToast({ open: true, generating: false });
            }
        } else if (modalVisibilityFlags?.showPdfViewer) {
            setOpenPDFModal(false);
            setPdfGenerateToast({ open: true, generating: true });
            setFactSheetsMissingToast({ open: false, generating: false });
            dispatch(updateGlobalLoaderState(true));
            try {
                await triggerBackendDownload();
            } finally {
                dispatch(updateGlobalLoaderState(false));
                // Safety net: if the download path never resolved the toast,
                // dismiss the in-progress one so it doesn't stay stuck.
                setPdfGenerateToast((prev) => (prev.generating ? { open: false, generating: false } : prev));
            }
        }
    };
    const disablePDFGenerate = useMemo(
        () =>
            !adviceGoals.global?.enablePDFGenerate ||
            adviceGoals?.global?.status !== STATUSVARIANTS.COMPLETE ||
            !isPlanExecutionReady,
        [adviceGoals.global?.enablePDFGenerate, adviceGoals.global?.status, isPlanExecutionReady]
    );

    // Stabilize the retry callback and memoize the PlanExecutionStep element so ordinary re-renders
    // of this section (e.g. loader toggles) don't cascade into PlanExecutionStep - React bails out
    // when it receives the same element reference. triggerPlanExecution is read via a ref so the
    // callback stays stable even though the prop is a new function each parent render.
    const triggerPlanExecutionRef = useRef(triggerPlanExecution);
    triggerPlanExecutionRef.current = triggerPlanExecution;
    const handleRetryPlanExecution = useCallback(() => triggerPlanExecutionRef.current(true), []);
    const planExecutionStep = useMemo(
        () => <PlanExecutionStep retryPlanExecution={handleRetryPlanExecution} />,
        [handleRetryPlanExecution]
    );
    return (
        <div style={{ maxWidth: '100%' }}>
            <StickyRegion>
                <StyledBanner />
                <Grid container justifyContent="space-between" alignItems="center" sx={{ paddingTop: '16px' }}>
                    <StyleDivWithStatus>
                        <Typography
                            variant="headers.h2"
                            color="semantic.text.brand"
                            sx={{ fontWeight: 700, lineHeight: '38px' }}
                            t="CLIENT_ONBOARDING_STEP6"
                        ></Typography>
                        <InfoTooltip message={adviceGoals?.global?.status === STATUSVARIANTS.COMPLETE ? t('STATUS_PILL_COMPLETE_TEXT')
                            : t('PLAN_EXECUTION_DRAFT_TOOLTIP')} placement="bottom-start">
                            <StatusChip>
                                {adviceGoals?.global?.status === STATUSVARIANTS.COMPLETE
                                    ? t('COMPLETE_TEXT')
                                    : t('INCOMPLETE_DRAFT_TEXT')}
                            </StatusChip>
                        </InfoTooltip>
                    </StyleDivWithStatus>
                    <Grid
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: '24px'
                        }}
                    >
                        <InfoTooltip message={t('SAVE_PLAN_TOOLTIP')} placement="bottom">
                            <SavePlanButton
                                variant={'contained'}
                                onClick={handleSavePlan}
                                disabled={
                                    (goalsWithMissingDates && goalsWithMissingDates?.length > 0) ||
                                    Boolean(goalDataRecomendation) ||
                                    !adviceClient?.enableConfirmConsoleNavigation ||
                                    adviceGoals?.global?.status === STATUSVARIANTS.COMPLETE ||
                                    !isPlanExecutionReady
                                }
                            >
                                {t('CLIENT_ONBOARDING_STEP5')}
                            </SavePlanButton>
                        </InfoTooltip>
                        <InfoTooltip message={t('PROPOSAL_FACTSHEET_TOOLTIP')} placement="bottom">
                            <StyledBasicButton
                                $isEnabled={!disablePDFGenerate}
                                theme={theme}
                                variant={'outlined'}
                                disabled={disablePDFGenerate}
                                onClick={() => {
                                    handlePdfGenerationClick();
                                }}
                                loading={openPDFModal}
                            >
                                <ArrowDownTrayIcon />
                                {t('DOWNLOAD_PROPOSAL')}
                            </StyledBasicButton>
                        </InfoTooltip>
                    </Grid>
                </Grid>
                <Typography
                    variant="body.large"
                    label={t('PLAN_EXECUTION_SUBTITLE')}
                    sx={{
                        color: theme.palette.semantic.text.default,
                        marginTop: '32px',
                        marginBottom: '32px',
                        width: '100%'
                    }}
                />
                {isStalePlan && onRefreshPlan && <StalePlanBanner onRefresh={onRefreshPlan} />}
                {savePlanError && (
                    <StyledBanner>
                        <Banner
                            type="error"
                            messageNode={
                                <Trans
                                    i18nKey="SAVE_PLAN_FAILED_BANNER"
                                    components={{
                                        cta: (
                                            <a
                                                href={`mailto:${supportEmail}`}
                                                style={{
                                                    color: theme.palette.semantic.bd.brand,
                                                    textDecoration: 'underline',
                                                    textUnderlineOffset: '2px',
                                                    fontWeight: 600,
                                                    cursor: 'pointer'
                                                }}
                                            />
                                        )
                                    }}
                                />
                            }
                        />
                    </StyledBanner>
                )}
            </StickyRegion>
            {planExecutionStep}
        </div>
    );
};

export default PlanExecutionSection;
